import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Plus, Truck } from "lucide-react";
import { toast } from "sonner";
import { gbp, imgUrl } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { T } from "@/constants/testIds";

export default function ProductCard({ product, className = "" }) {
  const navigate = useNavigate();
  const { add } = useCart();
  const img = imgUrl((product.images || [])[0]);
  const sizes = product.sizes || ["M"];

  const open = () => navigate(`/product/${product.id}`);
  const quickAdd = (e) => {
    e.stopPropagation();
    add(product, sizes[1] || sizes[0], (product.colours || [])[0] || "Black");
    toast.success(`${product.name} added to bag`);
  };

  return (
    <motion.article
      data-testid={T.productCard(product.id)}
      onClick={open}
      whileHover="hover"
      className={`group relative cursor-pointer overflow-hidden border border-stone-200 bg-[#FFFFFF] transition-shadow hover:shadow-[0_0_30px_rgba(229,46,57,0.18)] ${className}`}
    >
      <div className="relative aspect-[4/5] overflow-hidden">
        {img ? (
          <img
            src={img}
            alt={product.name}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-[#F1EEE6] font-display text-4xl uppercase text-stone-300">
            V.I.A
          </div>
        )}
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#FAF8F4]/90 via-transparent to-transparent" />
        {product.tag && (
          <span className="absolute left-3 top-3 bg-[#E52E39] px-2 py-1 font-mono text-[9px] font-semibold uppercase tracking-[0.18em] text-white">
            {product.tag}
          </span>
        )}
        <motion.button
          data-testid={T.productAddToCart(product.id)}
          onClick={quickAdd}
          className="absolute bottom-3 right-3 flex h-11 w-11 items-center justify-center border border-stone-300 bg-white/90 text-white backdrop-blur-md transition-colors hover:border-[#E52E39] hover:bg-[#E52E39] hover:text-white"
          variants={{ hover: { opacity: 1, y: 0 } }}
          initial={{ opacity: 0, y: 8 }}
          transition={{ duration: 0.25 }}
          aria-label={`Quick add ${product.name}`}
        >
          <Plus className="h-5 w-5" />
        </motion.button>
        <div className="absolute bottom-3 left-3 flex gap-1 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
          {sizes.slice(0, 5).map((s) => (
            <span key={s} className="border border-stone-300 bg-black/50 px-1.5 py-0.5 font-mono text-[9px] text-stone-600">
              {s}
            </span>
          ))}
        </div>
      </div>
      <div className="space-y-2 p-4">
        <div className="flex items-baseline justify-between gap-3">
          <h3 className="font-display text-lg font-bold uppercase leading-tight tracking-wide text-stone-950">
            {product.name}
          </h3>
          <span className="font-mono text-sm font-semibold text-[#C79500]">{gbp(product.price)}</span>
        </div>
        <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-wider text-stone-500">
          <Truck className="h-3.5 w-3.5 shrink-0 text-[#C79500]" />
          <span>{product.delivery_time}</span>
        </div>
      </div>
    </motion.article>
  );
}
